/**
 * The Signal Diet: how a reader's attention is actually distributed across the seven anchor
 * fields, plus the blindspot (the field they read least). Pure computation from locally seen
 * cards; fail closed below a minimum history so the wheel never renders on noise.
 * House style: no em dashes or en dashes anywhere in copy.
 */

export const ANCHOR_CATEGORIES = [
  "Money & Work",
  "Technology & Tools",
  "Science & Systems",
  "Power & Policy",
  "Culture & Curiosity",
  "Sport & Play",
  "Planet & Environment",
] as const;

export type Anchor = (typeof ANCHOR_CATEGORIES)[number];

export interface DietSlice {
  category: Anchor;
  count: number;
  /** Fraction of all categorised reads, 0..1. */
  share: number;
}

export interface SignalDiet {
  slices: DietSlice[];
  total: number;
  top: DietSlice;
  /** The least-read field, the reader's blindspot. */
  blindspot: DietSlice;
  /** True when the diet is genuinely lopsided (top field over half of everything). */
  lopsided: boolean;
}

export const MIN_READS_FOR_DIET = 10;

/** Compute the reader's diet across the anchors, or null when history is too thin. */
export function computeDiet(categoryCounts: Record<string, number>): SignalDiet | null {
  const slices: DietSlice[] = ANCHOR_CATEGORIES.map((category) => ({
    category,
    count: Math.max(0, Math.floor(categoryCounts[category] ?? 0)),
    share: 0,
  }));
  const total = slices.reduce((s, x) => s + x.count, 0);
  if (total < MIN_READS_FOR_DIET) return null;
  for (const s of slices) s.share = s.count / total;
  const sorted = [...slices].sort((a, b) => b.count - a.count);
  const top = sorted[0];
  // The blindspot prefers a field with zero reads; else the thinnest one. Stable order
  // breaks ties so the pick does not jump between renders.
  const blindspot = [...slices].sort((a, b) => a.count - b.count)[0];
  return {
    slices,
    total,
    top,
    blindspot,
    lopsided: top.share > 0.5,
  };
}

/** Conic-gradient stops for the diet wheel, one stop pair per non-empty slice. */
export function dietGradient(diet: SignalDiet, colors: Record<string, string>): string {
  let acc = 0;
  const stops: string[] = [];
  for (const s of diet.slices) {
    if (s.count === 0) continue;
    const from = acc * 360;
    acc += s.share;
    const to = acc * 360;
    const color = colors[s.category] || "#999";
    stops.push(`${color} ${from.toFixed(1)}deg ${to.toFixed(1)}deg`);
  }
  return `conic-gradient(${stops.join(", ")})`;
}
